<template>
  <div
    ref="stImage"
    class="st-image-wrap max-w-full relative"
    :style="divStyle"
  >
    <div
      ref="placeholder"
      class="stImage-loading-placeholder overflow-hidden rounded-lg pointer-events-none top-0 right-0"
      :style="placeholderStyle"
    >
      <BlurHashImage
        v-if="showBlurHash"
        ref="blurhashImage"
        class="blurhash-image absolute transition-opacity duration-300 ease-in overflow-hidden rounded-lg"
        :hash="blurHash"
        :style="blurhashImageStyle"
        :h="respHeight"
        :w="respWidth"
      />
      <img
        v-show="imageLoaded"
        ref="imgNode"
        class="rounded-lg"
        :alt="alt"
        :width="respWidth"
        :height="respHeight"
        :style="imgStyle"
      />
    </div>
  </div>
</template>
<script>
import BlurHashImage from '@/components/widgets/BlurHashImage';
import generateSrcUrl from '@/utilities/generateSrcUrl';
import EventBus from '@/utilities/eventBus';

export default {
  components: {
    BlurHashImage,
  },
  props: {
    imgsrc: {
      type: String,
      required: true,
    },
    blurHash: {
      type: String,
      required: false,
      default: '',
    },
    width: {
      type: Number,
      required: false,
      default: 0,
    },
    height: {
      type: Number,
      required: false,
      default: 0,
    },
    alt: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      imageLoaded: false,
      observer: null,
      divStyle: {
        width: `${this.respWidth}px`,
        height: `${this.respHeight}px`,
      },
      options: {
        root: null,
        rootMargin: '0px',
        threshold: [0.2],
      },
      placeholderStyle: {
        opacity: '1',
        height: `${this.respHeight}px`,
        width: `${this.respWidth}px`,
      },
      blurhashImageStyle: {
        opacity: 1,
      },
      isInViewport: false,
      respWidth: this.width,
      respHeight: this.height,
      throttleTimeout: null,
      showBlur: true,
      opacityTimeout: null,
      blurHideTimeout: null,
      imgStyle: {
        opacity: 0,
      },
      srcWidth: 0,
    };
  },
  computed: {
    showBlurHash() {
      if (process.env.VUE_APP_ID === 'public') return false;
      return this.blurHash !== '' && this.isInViewport && this.showBlur;
    },
  },
  watch: {
    width() {
      this.onResizeHandler();
    },
  },
  beforeMount() {
    this.srcWidth = this.width;
  },
  mounted() {
    this.$nextTick(() => {
      this.onResizeHandler(true);
    });
    EventBus.$on('beforePrint', this.beforePrint);

    this.intersectObs();
    window.addEventListener('resize', this.onResizeHandler);
  },
  unmounted() {
    window.removeEventListener('resize', this.onResizeHandler);
    if (this.opacityTimeout) {
      clearTimeout(this.opacityTimeout);
    }
    if (this.blurHideTimeout) {
      clearTimeout(this.blurHideTimeout);
    }
  },
  methods: {
    onResizeHandler(skipSetImage = false) {
      this.respWidth = this.$refs?.stImage?.clientWidth;

      if (!skipSetImage || this.respWidth - this.srcWidth > 10) {
        const time = this.respHeight < this.height ? 500 : 150;
        this.throttleSetImage(time);
      }

      const aspectRatio = this.width / this.height;
      this.respHeight = this.respWidth / aspectRatio;

      this.placeholderStyle.height = `${this.respHeight}px`;
    },
    throttleSetImage(time = 150) {
      if (this.throttleTimeout !== null) {
        clearTimeout(this.throttleTimeout);
        this.throttleTimeout = null;
      }
      this.throttleTimeout = setTimeout(() => {
        this.setImage();
      }, time);
    },
    intersectHandler([entry], observer) {
      const { isIntersecting } = entry;
      if (isIntersecting) {
        this.setImage(observer);
      }
    },
    intersectObs() {
      this.$nextTick(() => {
        /* create an instance and pass the callback and options */
        this.observer = new IntersectionObserver(this.intersectHandler, this.options);
        /* finally observe the target element */
        this.observer.observe(this.$refs.stImage);
      });
    },
    beforePrint() {
      this.setImage();
      EventBus.$off('beforePrint', this.beforePrint);
    },
    setImage(observer = this.observer) {
      observer.disconnect();
      this.isInViewport = true;
      const image = new Image();
      this.srcWidth = this.respWidth;
      const src = generateSrcUrl(this.imgsrc, this.respWidth, this.respHeight);
      image.onload = () => {
        this.$nextTick(() => {
          if (this.$refs.imgNode) {
            this.$refs.imgNode.onload = () => {
              this.opacityTimeout = setTimeout(() => {
                this.imgStyle.opacity = 1;
                this.$nextTick(() => {
                  this.imageLoaded = true;
                  this.blurhashImageStyle.opacity = 0;
                });
                this.blurHideTimeout = setTimeout(() => {
                  this.showBlur = false;
                }, 300);
              }, 400);
            };
            Object.assign(this.$refs.imgNode, {
              src,
            });
          }
        });
      };
      image.src = src;
    },
  },
};
</script>
